/**
 * Role-based navigation, mirroring FOCUS_Admin_Panel_v2.
 *
 * <p>Profile and sign-out are deliberately absent: they are things you do to your own
 * account, and they hang off the avatar in the header. This list is for places in the
 * product.
 */

/**
 * The back office. Super Admin and Support Executive walk the same product: Support
 * resolves what happens on the platform, and answering a ticket about a code, a school
 * or a verification means being able to reach the screen it happened on.
 *
 * <p>The Content Library is on this list for both and writable by one — the screen
 * itself drops its add and edit controls for Support, and the API refuses them.
 */
const BACK_OFFICE = [
  { path: '/', label: 'Dashboard', icon: 'grid' },
  { path: '/verification', label: 'Teacher Verification', icon: 'user-check', module: 'Module 2' },
  { path: '/schools', label: 'School Management', icon: 'building', module: 'Module 3' },
  { path: '/adoption', label: 'Adoptions', icon: 'grid', module: 'Module 4' },
  { path: '/codes', label: 'Activation Codes', icon: 'key', module: 'Module 7' },
  { path: '/content', label: 'Content Library', icon: 'folder' },
  { path: '/security', label: 'Access Log', icon: 'shield' },
  { path: '/renewals', label: 'Renewals', icon: 'clock' },
  { path: '/reports', label: 'Reports', icon: 'chart' },
  { path: '/registered', label: 'Registered Users', icon: 'user-check' },
  { path: '/users', label: 'Internal Users', icon: 'users' },
  { path: '/masters', label: 'Masters', icon: 'pin' },
];

export const NAV = {
  SUPER_ADMIN: BACK_OFFICE,
  // The same product, and the same order, so a screenshot from one desk matches the
  // other. Only the dashboard behind "/" differs, and only because Support has one of
  // their own worth keeping.
  SUPPORT_EXECUTIVE: BACK_OFFICE,
  SALES_REP: [
    { path: '/', label: 'Dashboard', icon: 'grid' },
    { path: '/verification', label: 'Teacher Verification', icon: 'user-check', module: 'Module 2' },
    { path: '/schools', label: 'My Schools', icon: 'building', module: 'Module 3' },
    { path: '/adoption', label: 'Adoptions', icon: 'grid', module: 'Module 4' },
    { path: '/codes', label: 'Activation Codes', icon: 'key', module: 'Module 7' },
    { path: '/renewals', label: 'Renewals', icon: 'clock' },
    { path: '/reports', label: 'Reports', icon: 'chart' },
  ],
  TEACHER: [
    { path: '/', label: 'Dashboard', icon: 'grid' },
    { path: '/repository', label: 'Resource Repository', icon: 'folder', module: 'Module 8' },
    { path: '/codes', label: 'Student Codes', icon: 'key', module: 'Module 7' },
    { path: '/devices', label: 'My Devices', icon: 'devices' },
  ],
  STUDENT: [
    { path: '/', label: 'My Dashboard', icon: 'grid' },
    { path: '/library', label: 'My Library', icon: 'folder', module: 'Module 5' },
    { path: '/devices', label: 'My Devices', icon: 'devices' },
  ],
};

export function navFor(role) {
  return NAV[role] ?? [];
}

/**
 * Titles for screens the sidebar does not list — reached from the account menu, from a
 * row in a table, or from a link on a dashboard. Without these the header reads "FOCUS"
 * on a real page, which says less than nothing.
 *
 * <p>Prefixes, longest first, so `/schools/:id` matches before `/schools` would.
 */
const OFF_NAV = [
  ['/verification/', 'Teacher Review'],
  ['/schools/', 'School'],
  ['/library/', 'Book'],
  ['/profile', 'Profile & Settings'],
  ['/devices', 'My Devices'],
];

/** What the header should say for this path, whichever way the user arrived. */
export function titleFor(role, pathname) {
  const listed = navFor(role).find((item) => item.path === pathname);
  if (listed) return listed.label;
  return OFF_NAV.find(([prefix]) => pathname.startsWith(prefix))?.[1] ?? 'FOCUS';
}
